import type { CountryCode } from './types';
import { getCountryByCode, getUniversities, getUniversityById, getEventsForUniversity, getAvailableAcademicYears, getMinEventsThreshold } from './data-source';
import { YEAR, getPhraseEntry, getTemplateStr, getLangLabel, getLangLabelEn } from './i18n-helpers';
import { buildLocalizedSlug, buildLocalizedDisplay } from './slug-word-order';
import { formatUniversityCityName } from './university-utils';
import { matchUniversityYearRedirect } from './university-year-redirect';

export interface UniversitySlugEntry {
  universityId: string;
  lang: string;
  slug: string;
  pageTitle: string;
  pageDescription: string;
  h2Title: string;
  academicYear: number;
}

function getAcademicYearRange(universityId: string, academicYear?: number): string {
  if (academicYear) return `${academicYear}/${academicYear + 1}`;

  const events = getEventsForUniversity(universityId);
  if (events.length === 0) return `${YEAR}/${YEAR + 1}`;

  const years = events.map(e => parseInt(e.dateStart.slice(0, 4), 10));
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  return minYear === maxYear ? `${minYear}/${minYear + 1}` : `${minYear}/${maxYear}`;
}

function buildUniversitySlug(uniNameSlug: string, lang: string, yearRange: string): string {
  const phrase = getPhraseEntry('academic', lang);
  const yearSlug = yearRange.replace('/', '-');
  return buildLocalizedSlug({ subject: uniNameSlug, phrase: phrase.slug, year: yearSlug }, lang);
}

function getUniversityLanguages(countryCode: CountryCode): string[] {
  const country = getCountryByCode(countryCode);
  const localLang = country?.localLang;
  const langs = ['en'];
  if (localLang && !langs.includes(localLang)) langs.push(localLang);
  return langs;
}

function getCountryNameForCode(code: CountryCode): string {
  const country = getCountryByCode(code);
  return country?.name ?? code;
}

function buildUniversityEntry(
  universityId: string,
  name: string,
  abbrev: string,
  city: string,
  lang: string,
  eventCount: number,
  academicYear?: number,
  countryName?: string,
): UniversitySlugEntry {
  const uniNameSlug = name.toLowerCase().replace(/\s+/g, '-');
  const yearRange = getAcademicYearRange(universityId, academicYear);
  const slug = buildUniversitySlug(uniNameSlug, lang, yearRange);
  const cityDisplay = formatUniversityCityName(city);
  const phrase = getPhraseEntry('academic', lang);
  const country = countryName ?? getCountryNameForCode(
    (getUniversityById(universityId)?.country ?? 'ID') as CountryCode
  );

  // Tightened title (almanak-uj7qp.12): drop full name + brand suffix.
  // SERP-ready format: "<ABBREV> <Academic Calendar> <yearRange>" — typically
  // 30–48 chars vs. the old 80–100 chars that truncated in Google. Google
  // auto-appends the site name itself, so the trailing "— vacant.day" was
  // redundant.
  const pageTitle = buildLocalizedDisplay({ subject: abbrev, phrase: phrase.display, year: yearRange }, lang);

  return {
    universityId,
    lang,
    slug,
    pageTitle,
    pageDescription: getTemplateStr('uni_desc', lang, { name, abbrev, count: eventCount, city: cityDisplay, year_range: yearRange, country_name: country }),
    h2Title: getTemplateStr('uni_h2', lang, { name, abbrev, year_range: yearRange }),
    academicYear: academicYear ?? YEAR,
  };
}

// --- University registry cache ---

let _uniSlugCache: Record<string, { universityId: string; lang: string; academicYear: number }> | null = null;
let _uniReverseCache: Record<string, string> | null = null;

export function ensureUniversityBuilt() {
  if (_uniSlugCache) return;

  const universities = getUniversities();
  const cache: Record<string, { universityId: string; lang: string; academicYear: number }> = {};
  const reverse: Record<string, string> = {};

  for (const uni of universities) {
    const langs = getUniversityLanguages(uni.country);
    const countryName = getCountryNameForCode(uni.country);
    const years = getAvailableAcademicYears(uni.id);
    const yearsToGenerate = years.length > 0 ? years : [YEAR];

    for (const year of yearsToGenerate) {
      const entityKey = `${uni.id}:${year}`;
      const eventCount = getEventsForUniversity(entityKey).length;
      if (eventCount < getMinEventsThreshold('university')) continue;

      for (const lang of langs) {
        const entry = buildUniversityEntry(uni.id, uni.name, uni.abbrev, uni.city, lang, eventCount, year, countryName);
        cache[entry.slug] = { universityId: uni.id, lang, academicYear: year };
        reverse[`${uni.id}:${year}:${lang}`] = entry.slug;
      }
    }
  }

  if (Object.keys(cache).length === 0) return;

  _uniSlugCache = cache;
  _uniReverseCache = reverse;
}

// --- Public API ---

export function getAllUniversitySlugs(): string[] {
  ensureUniversityBuilt();
  return Object.keys(_uniSlugCache ?? {});
}

export function getUniversitySlugEntry(slug: string): UniversitySlugEntry | null {
  ensureUniversityBuilt();
  const match = _uniSlugCache?.[slug];
  if (!match) return null;

  const uni = getUniversityById(match.universityId);
  if (!uni) return null;

  const entityKey = `${uni.id}:${match.academicYear}`;
  const eventCount = getEventsForUniversity(entityKey).length;
  return buildUniversityEntry(uni.id, uni.name, uni.abbrev, uni.city, match.lang, eventCount, match.academicYear, getCountryNameForCode(uni.country));
}

export function getAlternateUniversityLanguages(universityId: string, academicYear?: number): Array<{ lang: string; slug: string; label: string; labelEn?: string }> {
  ensureUniversityBuilt();
  const year = academicYear ?? YEAR;
  // University pages are English-only for hreflang. The localized (local-language)
  // slugs currently mirror the English page — untranslated chrome/FAQ, English
  // canonical — and resolve to English, so advertising them as `id`/local-lang
  // alternates points Google at pages that aren't really localized (a broken-
  // hreflang / phantom-localization defect). Emit only the English alternate
  // until genuinely-localized university pages exist. (almanak-phxn)
  const enSlug = _uniReverseCache?.[`${universityId}:${year}:en`];
  if (!enSlug) return [];
  return [{ lang: 'en', slug: enSlug, label: getLangLabel('en'), labelEn: getLangLabelEn('en') }];
}

export function getUniversitySlugForId(universityId: string, lang: string = 'en', academicYear?: number): string {
  ensureUniversityBuilt();
  const year = academicYear ?? YEAR;
  return _uniReverseCache?.[`${universityId}:${year}:${lang}`] || _uniReverseCache?.[`${universityId}:${year}:en`] || '';
}

/** Internal: access the raw university slug cache for cross-registry functions */
export function _getUniSlugCache(): Record<string, { universityId: string; lang: string; academicYear: number }> {
  ensureUniversityBuilt();
  return _uniSlugCache ?? {};
}

let _uniEnSlugsForRedirect: Array<{ slug: string; academicYear: number }> | null = null;

/**
 * Maps a wrong-year university academic-calendar slug to the current-cycle
 * page (almanak-n0xc). Crawlers speculate on the year pattern — probing
 * `{uni}-academic-calendar-2027-2028` from the current-cycle URL before the
 * next cycle publishes — and would otherwise 404. Only unpublished years reach
 * this (a generated year resolves in resolveSlug first). English slugs only,
 * matching the observed 404s. Returns null when the stem is not a known
 * university (so non-university year-range slugs are unaffected).
 */
export function findUniversityYearRedirect(slug: string): string | null {
  ensureUniversityBuilt();
  // Only memoize off a fully-built registry — _uniSlugCache stays null when the
  // build ran with empty data, and caching [] here would poison later requests
  // that have real data (cf. almanak-eo3).
  if (!_uniSlugCache) return null;
  if (!_uniEnSlugsForRedirect) {
    _uniEnSlugsForRedirect = Object.entries(_uniSlugCache)
      .filter(([, meta]) => meta.lang === 'en')
      .map(([genSlug, meta]) => ({ slug: genSlug, academicYear: meta.academicYear }));
  }
  return matchUniversityYearRedirect(slug, _uniEnSlugsForRedirect, YEAR);
}
